/**
 * Dragging icons on the Desktop — with pointer events, not HTML5 drag-and-
 * drop. Native drag-and-drop stays reserved for files dropped from the
 * computer (uploads); an internal drag never touches `dataTransfer`, and
 * icons cancel `dragstart`, so the two can't collide.
 *
 * A drag can end over:
 *   - a canvas (the desktop, or a folder window)  → { container, cell }
 *   - a folder icon                               → { folder }
 *   - the Trash icon                              → { trash }
 * found on pointer-up (and on every move, for hover feedback) with
 * `document.elementsFromPoint`: the topmost element carrying
 * `data-desktop-drop` wins, so a window over the desktop takes the drop.
 *
 * What a drop MEANS is not decided here — the Desktop's `evaluate` says
 * whether it's allowed (for the cursor and tooltip), and `drop` performs it
 * through the same placement controller every other view uses.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Cell } from '../../lib/desktop-layout';
import { CANVAS_PAD, CELL_H, CELL_W, cellAtPoint, type DesktopItem } from './desktop-model';

export type DropTarget =
  /** `cell` is null for a keyboard paste: no spot was chosen, so the files
   *  auto-arrange in their new folder. */
  | { kind: 'container'; container: string; cell: Cell | null }
  | { kind: 'folder'; folderId: string }
  | { kind: 'trash' };

export interface DragSession {
  /** The container the items are dragged out of. */
  container: string;
  /** Dragged items, the one under the pointer first. */
  items: DesktopItem[];
  /** Each item's cell offset from the first, to keep a group's shape. */
  offsets: Map<string, { dc: number; dr: number }>;
  /** Pointer offset inside the first item's cell. */
  grab: { dx: number; dy: number };
  pointer: { x: number; y: number };
  target: DropTarget | null;
  allowed: boolean;
  /** Why not, when not allowed. */
  reason: string | null;
}

export interface DropEvaluation {
  allowed: boolean;
  reason: string | null;
}

/**
 * Where the pointer is during a drag — a tiny external store, so only the
 * drag ghost re-renders on every frame. The session itself (what the canvases
 * read) changes only when the target or its verdict does.
 */
export interface PointerStore {
  get: () => { x: number; y: number } | null;
  subscribe: (listener: () => void) => () => void;
}

function createPointerStore() {
  let pointer: { x: number; y: number } | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => pointer,
    set: (next: { x: number; y: number } | null) => {
      pointer = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const sameTarget = (a: DropTarget | null, b: DropTarget | null): boolean => {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'trash') return true;
  if (a.kind === 'folder') return a.folderId === (b as typeof a).folderId;
  const other = b as typeof a;
  return (
    a.container === other.container &&
    (a.cell?.c ?? -1) === (other.cell?.c ?? -1) &&
    (a.cell?.r ?? -1) === (other.cell?.r ?? -1)
  );
};

const draggedKeys = (items: readonly DesktopItem[]) => new Set(items.map((i) => i.key));

/**
 * The drop target under a viewport point. An icon being dragged is never its
 * own target — nudging a folder or the Trash lands on the canvas beneath it,
 * which is an ordinary rearrangement.
 */
export function hitTest(
  x: number,
  y: number,
  grab: { dx: number; dy: number },
  dragged: ReadonlySet<string> = new Set(),
): DropTarget | null {
  if (typeof document.elementsFromPoint !== 'function') return null;
  for (const el of document.elementsFromPoint(x, y)) {
    let drop = (el as HTMLElement).closest?.('[data-desktop-drop]') as HTMLElement | null;
    if (drop?.dataset.desktopKey && dragged.has(drop.dataset.desktopKey)) {
      // The dragged icon itself: look past it to the canvas it sits on.
      drop = drop.parentElement?.closest('[data-desktop-drop]') as HTMLElement | null;
    }
    if (!drop) continue;
    const spec = drop.dataset.desktopDrop ?? '';
    if (spec === 'trash') return { kind: 'trash' };
    if (spec.startsWith('folder:')) return { kind: 'folder', folderId: spec.slice(7) };
    if (spec.startsWith('container:')) {
      const container = spec.slice(10);
      // A folder window flows by its view: a drop there has no cell and
      // auto-arranges.
      if (drop.dataset.free === 'false') return { kind: 'container', container, cell: null };
      const rect = drop.getBoundingClientRect();
      const columns = Number(drop.dataset.columns) || 1;
      const g = {
        cellW: Number(drop.dataset.cellW) || CELL_W,
        cellH: Number(drop.dataset.cellH) || CELL_H,
        pad: Number(drop.dataset.pad) || CANVAS_PAD,
      };
      // Where the dragged icon's centre would land.
      const cell = cellAtPoint(
        x - rect.left - grab.dx + g.cellW / 2,
        y - rect.top - grab.dy + g.cellH / 2,
        columns,
        g,
      );
      return { kind: 'container', container, cell };
    }
  }
  return null;
}

export function useCanvasDrag(handlers: {
  evaluate: (session: DragSession, target: DropTarget | null) => DropEvaluation;
  drop: (session: DragSession, target: DropTarget) => void;
  /** Released over a target that refused — say why, since a quick drag may
   *  never have shown the tooltip. */
  refused: (reason: string) => void;
}) {
  const [session, setSession] = useState<DragSession | null>(null);
  const sessionRef = useRef<DragSession | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const frame = useRef<number | null>(null);
  const pointerStore = useMemo(createPointerStore, []);

  const update = useCallback(
    (next: DragSession | null) => {
      sessionRef.current = next;
      setSession(next);
      if (!next) pointerStore.set(null);
    },
    [pointerStore],
  );

  const begin = useCallback(
    (init: Omit<DragSession, 'target' | 'allowed' | 'reason'>) => {
      const target = hitTest(init.pointer.x, init.pointer.y, init.grab, draggedKeys(init.items));
      const draft: DragSession = { ...init, target, allowed: false, reason: null };
      const evaluation = handlersRef.current.evaluate(draft, target);
      pointerStore.set(init.pointer);
      update({ ...draft, ...evaluation });
    },
    [pointerStore, update],
  );

  const cancel = useCallback(() => update(null), [update]);

  const active = session !== null;
  useEffect(() => {
    if (!active) return;
    const move = (e: PointerEvent) => {
      const current = sessionRef.current;
      if (!current) return;
      const pointer = { x: e.clientX, y: e.clientY };
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        const latest = sessionRef.current;
        if (!latest) return;
        pointerStore.set(pointer);
        const target = hitTest(pointer.x, pointer.y, latest.grab, draggedKeys(latest.items));
        // The canvases only care when the target (or its verdict) changes.
        if (sameTarget(target, latest.target)) return;
        const evaluation = handlersRef.current.evaluate({ ...latest, pointer }, target);
        update({ ...latest, pointer, target, ...evaluation });
      });
    };
    const up = (e: PointerEvent) => {
      const current = sessionRef.current;
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      update(null);
      if (!current) return;
      const target = hitTest(e.clientX, e.clientY, current.grab, draggedKeys(current.items));
      if (!target) return;
      const finalSession = { ...current, pointer: { x: e.clientX, y: e.clientY }, target };
      const evaluation = handlersRef.current.evaluate(finalSession, target);
      if (evaluation.allowed) handlersRef.current.drop(finalSession, target);
      else if (evaluation.reason) handlersRef.current.refused(evaluation.reason);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        update(null);
      }
    };
    const stop = () => update(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('keydown', key, true);
    window.addEventListener('blur', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('blur', stop);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [active, pointerStore, update]);

  return { session, begin, cancel, pointer: pointerStore as PointerStore };
}
